import { createFileRoute } from "@tanstack/react-router";
import {
  Badge,
  Container,
  Grid,
  HStack,
  PageHeader,
  ScrollRegion,
  Spacer,
  Surface,
  VDivider,
  VStack,
} from "@itoresearch/design-system";
import { ChartDemo, SeriesGallery } from "../components/design/chart-demo";
import { ComponentsDemo } from "../components/design/components-demo";
import { PerspectiveDemo } from "../components/design/perspective-demo";

export const Route = createFileRoute("/design")({
  component: DesignShowcasePage,
});

function DesignShowcasePage() {
  return (
    <Container className="py-2" width="full">
      <VStack gap="lg">
        <PageHeader
          title="Design system"
          description="Components, layout primitives, charts, and tables for the Ito design language."
          meta={
            <>
              <Badge variant="outline">Components</Badge>
              <Badge variant="outline">Charts</Badge>
              <Badge variant="outline">Data grid</Badge>
              <Badge variant="outline">Themes</Badge>
            </>
          }
        />

        <ChartDemo />

        <Grid minItemWidth="560px">
          <PerspectiveDemo />
        </Grid>

        <ComponentsDemo />

        <Section title="Series gallery">
          <SeriesGallery />
        </Section>

        <Section
          title="Layout primitives"
          description="Stack, Grid, Surface, Container, PageHeader, Divider — all density-aware"
        >
          <Surface level="raised" padding="lg">
            <VStack gap="md">
              <HStack gap="sm" align="center">
                <Badge>Surface</Badge>
                <span className="text-sm text-muted-foreground">
                  HStack · VDivider · Spacer
                </span>
                <Spacer />
                <span
                  data-numeric
                  className="font-mono text-2xs text-muted-foreground"
                >
                  right-aligned via Spacer
                </span>
              </HStack>
              <VDivider />
              <Grid cols={3} gap="sm">
                {["Cell A", "Cell B", "Cell C"].map((c) => (
                  <Surface key={c} level="flat" padding="sm">
                    <span
                      data-numeric
                      className="font-mono text-xs text-muted-foreground"
                    >
                      {c}
                    </span>
                  </Surface>
                ))}
              </Grid>
            </VStack>
          </Surface>
        </Section>

        <Section title="ScrollRegion">
          <Surface level="flat">
            <ScrollRegion maxHeight="160px">
              <VStack gap="xs" className="p-md">
                {Array.from({ length: 12 }, (_, i) => (
                  <HStack key={i} gap="sm" className="text-sm">
                    <span
                      data-numeric
                      className="font-mono text-2xs text-muted-foreground w-8"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>Scrollable row {i + 1}</span>
                  </HStack>
                ))}
              </VStack>
            </ScrollRegion>
          </Surface>
        </Section>
      </VStack>
    </Container>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-sm">
      <div>
        <h2 className="font-display text-sm tracking-tight">{title}</h2>
        {description && (
          <p className="text-2xs text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}
